import React from 'react';
import { Search, Paperclip, Star } from 'lucide-react';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { formatDateTime } from '../../../utils/appDateTime';

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'starred', label: 'Starred' },
  { id: 'important', label: 'Important' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
];

export default function CrmMailSearchBar({
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
}) {
  return (
    <div className="p-3 border-b border-border space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          id="crm-mail-search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search mail…"
          className="pl-9 h-9"
        />
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onFilterChange(f.id)}
            className={cn(
              'shrink-0 px-2.5 py-1 rounded-full text-xs border transition-colors',
              filter === f.id
                ? 'bg-blue-600 text-white border-blue-600'
                : 'border-border text-muted-foreground hover:bg-muted',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CrmMailList({
  messages,
  selectedId,
  loading,
  onSelect,
  folder,
}) {
  if (loading) {
    return <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>;
  }

  if (!messages.length) {
    return null;
  }

  return (
    <ul className="divide-y divide-border overflow-y-auto flex-1">
      {messages.map((msg) => {
        const fromName = msg.from?.name || msg.from?.email || msg.to?.[0]?.email || '—';
        const date = msg.received_at || msg.sent_at;
        const isSelected = selectedId === msg.id;

        return (
          <li key={msg.id}>
            <button
              type="button"
              onClick={() => onSelect(msg.id)}
              className={cn(
                'w-full text-left px-4 py-3 hover:bg-muted/60 transition-colors',
                isSelected && 'bg-blue-600/5 border-l-2 border-l-blue-600',
                !msg.is_read && folder === 'inbox' && 'bg-blue-50/50 dark:bg-blue-950/20 font-medium',
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-0.5">
                <span className={cn('text-sm truncate flex-1', !msg.is_read && 'font-semibold')}>
                  {folder === 'sent' || folder === 'drafts' ? `To: ${msg.to?.[0]?.email || '—'}` : fromName}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {date ? formatDateTime(date, { dateOnly: false, timeOnly: false }) : ''}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-0.5">
                {msg.is_starred && <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />}
                <span className="text-sm truncate">{msg.subject || '(no subject)'}</span>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground truncate flex-1">{msg.snippet}</p>
                {msg.has_attachments && <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
